// HU-45 · Mantener las plantillas de carta por autoridad (F-07, Sprint 2) y HU-45b (MG-ILE-914,
// tarea 45b-3, Sprint 3): campos combinables, error de publicación y estado por autoridad.
//
// Spec `05_analisis_tecnico/Farmacovigilancia/ui/MG-ILE-914.md`. Lo que esta pantalla hace:
//
//   · LISTA las autoridades con el estado de su plantilla que deriva el servidor
//     (`estado_de_plantilla` + `texto_de_plantilla`, HU-45b-C5): «SENASA · v2» o «Sin plantilla».
//     Antes la pantalla armaba ese texto a partir de la versión (§8.1 R1).
//   · CAMPOS QUE EL SISTEMA RELLENA (HU-45b-C1): la lista única del servidor, con su etiqueta. Es la
//     misma que valida la publicación y la que nombra el mensaje de C2. Se retira el mapeo local
//     `ETIQUETA_DEL_CAMPO` (§8.1 R2).
//   · PUBLICAR la versión siguiente desde un .docx o desde texto. Un campo que el sistema no sabe
//     rellenar lo rechaza el servidor (422, RN-55): el mensaje va bajo el archivo o el texto y no se
//     crea ninguna versión (C2). Aceptada, se refrescan el historial y las autoridades (§8.1 R3).
//   · HISTORIAL (C4): cada versión con su vigencia y descargable. VISTA PREVIA (C3): la misma
//     combinación que hará la carta del caso. El estado de cada versión (vigente, programada o
//     anterior) y su frase los da el servidor en el día del país (BUG-1101, `ui/MG-ILE-1101.md`).
//   · Solo el administrador: un 403 se muestra «No tienes acceso a esta opción», con el `h1`
//     (HU-51, tarea 51-9).
//   · POR AUTORIDAD (CC-02, bloque B3; RN-75 y RN-81, AT S3 §11.5.3): al elegir un país se piden
//     sus autoridades con la plantilla que hoy rige para cada una (`/autoridades`). Con una sola,
//     la pantalla es la de antes. Con varias, se elige la autoridad: historial, publicación y
//     vista previa son de SU serie (`?autoridad=`), y la que no tiene plantilla propia dice
//     «Sin plantilla: se usa la de SENASA» (texto del servidor). La predeterminada viaja sin
//     `?autoridad=`: las rutas por país son su alias.
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangle, Braces, CheckCircle2, Download, Eye, FileText, FileUp, History, Landmark, RotateCcw, Send,
} from 'lucide-react';
import {
  ACEPTA, Alert, Button, Card, Codigo, EmptyState, Field, Input, Mono, PageHeader, Radio, StatusBadge, SubidaDeArchivo, Textarea,
} from '../components/ui';
import { descargarBlob } from '../lib/entradas';
import Bandera from '../components/Banderas';
import { listarAutoridades } from '../api/autoridades';
import {
  archivoABase64, camposCombinables, descargarVersion, plantillasPorAutoridad, publicarDesdeArchivo, publicarTexto,
  versionesDePlantilla, vistaPrevia,
} from '../api/plantillas';
import type { CampoCombinable, CartaCombinada, Plantilla, PlantillaDeLaAutoridad } from '../api/plantillas';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import type { RespuestaDelApi } from '../tipos';
import { legible, MARCAS_DE_AUTORIDAD, tonoDelEstadoDePlantilla } from '../lib/etiquetas';

const SIN_ACCESO = 'No tienes acceso a esta opción';

/** Los datos de ejemplo de C2, para que la vista previa se pruebe sin abrir un caso. */
const DATOS_DE_EJEMPLO: Record<string, string> = {
  numero_caso: 'PE-001-2026',
  producto: 'Ivermectina 1 %',
  lote: 'L-2026-08',
  fecha_evento: '2026-09-01',
};

type Autoridad = {
  pais: string;
  nombre_pais: string;
  autoridad: string;
  estado_de_plantilla: string;
  texto_de_plantilla: string;
};

/** `numero_caso` → `numero-caso`: el sufijo del testid de cada dato (spec, kebab-case). */
const enKebab = (clave: string): string => clave.split('_').join('-');

function VistaPreviaDeCarta({ pais, codigo, autoridad, version, campos, etiquetaDe }: {
  /** CC-02: el código de una autoridad que no es la predeterminada; vacío, la predeterminada. */
  pais: string; codigo: string; autoridad: string; version: number; campos: string[];
  etiquetaDe: (clave: string) => string;
}) {
  const [datos, setDatos] = useState<Record<string, string>>(() =>
    Object.fromEntries(campos.map((c) => [c, DATOS_DE_EJEMPLO[c] ?? ''])),
  );
  const [carta, setCarta] = useState<CartaCombinada | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);

  useEffect(() => {
    setDatos(Object.fromEntries(campos.map((c) => [c, DATOS_DE_EJEMPLO[c] ?? ''])));
    setCarta(null);
  }, [campos, version]);

  const [ocupado, setOcupado] = useState(false);
  const preparar = async () => {
    setFallo(null);
    setOcupado(true);
    try {
      setCarta(await (codigo ? vistaPrevia(pais, datos, version, codigo) : vistaPrevia(pais, datos, version)));
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Card title={`Vista previa · ${autoridad || pais} · v${version}`} subtitle="La carta con los datos de un caso en cada hueco." icon={Eye}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {campos.map((campo) => (
          <Field key={campo} label={etiquetaDe(campo)} hint={<Mono>{`{{${campo}}}`}</Mono>}>
            <Input data-testid={`plantillas-previa-dato-${enKebab(campo)}`} value={datos[campo] ?? ''}
              onChange={(e) => setDatos((d) => ({ ...d, [campo]: e.target.value }))} />
          </Field>
        ))}
      </div>
      <div className="mt-3">
        <Button variant="secondary" icon={Eye} onClick={() => { preparar(); }} disabled={ocupado} data-testid="plantillas-previa-preparar">
          Preparar la carta
        </Button>
      </div>
      {fallo && <div className="mt-3"><Alert tone="danger" title="No se pudo preparar la carta" data-testid="plantillas-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
      {carta && (
        <div className="mt-4">
          <div data-testid="plantillas-previa-estado">
            {carta.lista_para_firmar ? (
              <StatusBadge tone="success" icon={CheckCircle2}>Lista para firmar</StatusBadge>
            ) : (
              <Alert tone="warning" title="La carta no se puede firmar hasta resolver los campos pendientes">
                Sin dato: {carta.pendientes.map((p) => <Mono key={p} className="mr-2">{p}</Mono>)}
              </Alert>
            )}
          </div>
          <pre data-testid="plantillas-previa-carta" className="mt-3 whitespace-pre-wrap text-meta bg-canvas border border-hairline rounded-xl p-4 font-sans">
            {carta.texto}
          </pre>
        </div>
      )}
    </Card>
  );
}

function PublicarNuevaVersion({ pais, codigo, campos, onPublicada }: {
  pais: string;
  /** CC-02: el código de una autoridad que no es la predeterminada; vacío, la predeterminada. */
  codigo: string;
  campos: CampoCombinable[];
  onPublicada: (p: Plantilla) => void;
}) {
  const [modo, setModo] = useState<'archivo' | 'texto'>('archivo');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [texto, setTexto] = useState('');
  const [desde, setDesde] = useState('');
  const [errorDelContenido, setErrorDelContenido] = useState<string | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const publicar = async () => {
    setFallo(null);
    setErrorDelContenido(null);
    // V-06: textos propuestos; antes eran un `throw new Error` que se pintaba como fallo.
    if (modo === 'archivo' && !archivo) { setErrorDelContenido('Elige el documento de Word (.docx).'); return; }
    if (modo === 'texto' && !texto.trim()) { setErrorDelContenido('El texto de la plantilla no puede quedar vacío.'); return; }
    setOcupado(true);
    try {
      // Sin código no se manda `?autoridad=`: la petición de la predeterminada es la de siempre.
      let publicada: Plantilla;
      if (modo === 'archivo' && archivo) {
        const contenido = await archivoABase64(archivo);
        publicada = codigo
          ? await publicarDesdeArchivo(pais, archivo.name, contenido, desde || undefined, codigo)
          : await publicarDesdeArchivo(pais, archivo.name, contenido, desde || undefined);
      } else {
        publicada = codigo
          ? await publicarTexto(pais, texto, desde || undefined, codigo)
          : await publicarTexto(pais, texto, desde || undefined);
      }
      setArchivo(null);
      setTexto('');
      onPublicada(publicada);
    } catch (e) {
      const f = aFallo(e);
      // HU-45b-C2: el 422 (campo que el sistema no sabe rellenar, o fecha anterior a la vigente)
      // va bajo el archivo o el texto, con el `detalle` del servidor. Lo demás, en el aviso.
      if (f.estado === 422) setErrorDelContenido(f.message);
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  const ayudaDeCampos = campos.length
    ? `Los huecos se escriben {{campo}}: ${campos.map((c) => `{{${c.clave}}}`).join(', ')}.`
    : 'Los huecos se escriben {{campo}}.';

  return (
    <Card title="Publicar la versión siguiente" icon={FileUp}
      subtitle="Editar es publicar una versión nueva: la anterior queda en el historial y las cartas ya emitidas conservan su texto.">
      <Field label="Origen de la versión" grupo data-testid="plantillas-form-origen">
        <div className="flex flex-wrap gap-2">
          <Radio name={`origen-${pais}-${codigo}`} value="archivo" checked={modo === 'archivo'} label="Desde un documento de Word"
            onChange={() => { setModo('archivo'); setErrorDelContenido(null); }} />
          <Radio name={`origen-${pais}-${codigo}`} value="texto" checked={modo === 'texto'} label="Desde texto"
            onChange={() => { setModo('texto'); setErrorDelContenido(null); }} />
        </div>
      </Field>
      <div className="mt-3">
        {modo === 'archivo' ? (
          <Field label="Documento de Word (.docx)" required hint={ayudaDeCampos} error={errorDelContenido ?? undefined}>
            <SubidaDeArchivo aria-label="Documento de Word" archivo={archivo} acepta={ACEPTA.word} maxMb={5}
              onElegir={(f) => { setArchivo(f); setErrorDelContenido(null); }} data-testid="plantillas-form-archivo" />
          </Field>
        ) : (
          <Field label="Texto de la plantilla" required hint={ayudaDeCampos} error={errorDelContenido ?? undefined}>
            <Textarea data-testid="plantillas-form-texto" rows={8} value={texto}
              onChange={(e) => { setTexto(e.target.value); setErrorDelContenido(null); }} />
          </Field>
        )}
      </div>
      {/* Con aire encima: el hint del documento y esta etiqueta se leían pegados. */}
      <div className="mt-4 pt-3 border-t border-hairline">
        <Field label="Vigente desde" hint="Sin fecha, rige desde hoy.">
          <Input type="date" data-testid="plantillas-form-desde" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </Field>
      </div>
      {fallo && <div className="mb-3"><Alert tone="danger" title="No se pudo publicar" data-testid="plantillas-error-envio" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
      <div className="flex justify-end">
        <Button icon={Send} onClick={() => { publicar(); }} disabled={ocupado} data-testid="plantillas-form-publicar">
          {ocupado ? 'Publicando…' : 'Publicar versión'}
        </Button>
      </div>
    </Card>
  );
}

export default function PlantillasDeCarta() {
  const [autoridades, setAutoridades] = useState<Autoridad[]>([]);
  const [campos, setCampos] = useState<CampoCombinable[]>([]);
  const [pais, setPais] = useState('');
  const [versiones, setVersiones] = useState<Plantilla[] | null>(null);
  // CC-02: las autoridades del país elegido y cuál de ellas se mira. `codigo` vacío es la
  // predeterminada, que viaja sin `?autoridad=` (las rutas por país son su alias).
  const [delPais, setDelPais] = useState<PlantillaDeLaAutoridad[] | null>(null);
  const [codigo, setCodigo] = useState('');
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);

  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => { vivo.current = false; };
  }, []);

  const cargarAutoridades = useCallback(async () => {
    setFallo(null);
    try {
      const [lista, respuestaDeCampos] = await Promise.all([listarAutoridades(), camposCombinables()]);
      if (!vivo.current) return;
      setAutoridades((lista as RespuestaDelApi[]).map((a) => ({
        pais: String(a.pais), nombre_pais: String(a.nombre_pais ?? a.pais),
        autoridad: String(a.autoridad ?? ''),
        estado_de_plantilla: String(a.estado_de_plantilla ?? ''),
        texto_de_plantilla: String(a.texto_de_plantilla ?? ''),
      })));
      setCampos(respuestaDeCampos?.campos ?? []);
    } catch (e) {
      if (vivo.current) setFallo(aFallo(e));
    } finally {
      if (vivo.current) setCargando(false);
    }
  }, []);

  useEffect(() => { cargarAutoridades(); }, [cargarAutoridades]);

  // `vivo` + lo pedido (país y autoridad): si el administrador cambia de autoridad antes de
  // que lleguen las versiones de la anterior, esa respuesta se descarta.
  const seriePedida = useRef('');
  const cargarVersiones = useCallback(async (paisPedido: string, codigoPedido: string) => {
    setFallo(null);
    const serie = `${paisPedido}/${codigoPedido}`;
    seriePedida.current = serie;
    try {
      const lista = codigoPedido
        ? await versionesDePlantilla(paisPedido, codigoPedido)
        : await versionesDePlantilla(paisPedido);
      if (!vivo.current || seriePedida.current !== serie) return;
      setVersiones(lista);
    } catch (e) {
      if (vivo.current && seriePedida.current === serie) setFallo(aFallo(e));
    }
  }, []);

  const paisDeLasAutoridades = useRef('');
  const cargarDelPais = useCallback(async (paisPedido: string) => {
    paisDeLasAutoridades.current = paisPedido;
    try {
      const lista = await plantillasPorAutoridad(paisPedido);
      if (!vivo.current || paisDeLasAutoridades.current !== paisPedido) return;
      setDelPais(lista);
    } catch (e) {
      if (vivo.current && paisDeLasAutoridades.current === paisPedido) setFallo(aFallo(e));
    }
  }, []);

  // Al cambiar de país se piden sus autoridades. La vuelta a la predeterminada (`codigo` vacío)
  // la hace el mismo clic que cambia el país, para que nunca se pidan las versiones de una
  // autoridad con el país de otra.
  useEffect(() => {
    setDelPais(null);
    if (pais) cargarDelPais(pais);
  }, [pais, cargarDelPais]);

  // Al cambiar de autoridad se VACÍA lo de la anterior antes de pedir lo nuevo: el
  // historial de SENASA se quedaba bajo el rótulo de ICA los segundos que tardaba la
  // respuesta (QA, 14-sep-2026).
  useEffect(() => {
    setVersiones(null);
    setAviso(null);
    if (pais) cargarVersiones(pais, codigo);
  }, [pais, codigo, cargarVersiones]);

  const descargar = async (version: number) => {
    setFallo(null);
    try {
      const archivo = codigo ? await descargarVersion(pais, version, codigo) : await descargarVersion(pais, version);
      const serie = codigo ? `${pais}_${codigo}` : pais;
      descargarBlob(archivo, `plantilla_${serie.toLowerCase()}_v${version}.docx`);
    } catch (e) {
      setFallo(aFallo(e));
    }
  };

  /** La etiqueta de un campo: la del servidor; un campo que no está en su lista sale legible (vacío del contrato). */
  const etiquetaDe = useCallback(
    (clave: string) => campos.find((c) => c.clave === clave)?.etiqueta ?? legible(clave),
    [campos],
  );

  // El encabezado es el mismo con permiso o sin el: lleva el `h1` y el titulo de la
  // pestana (WCAG 1.3.1 y 2.4.2). Con el 403 la pantalla se quedaba sin `h1` y la pestana
  // del navegador decia solo «Farmacovigilancia» (HU-51, tarea 51-9).
  const encabezado = (
    <PageHeader
      eyebrow="Administración · Plantillas de carta"
      hu="HU-45 · HU-45b · RN-05 / RN-24 / RN-37 / RN-55"
      title="Plantillas de carta"
      subtitle="La carta a cada autoridad sale de su propia plantilla, versionada. Elige una autoridad para ver su historial, publicar una versión y probarla con datos de un caso."
      icon={FileText}
    />
  );

  // C6: el servidor niega el mantenimiento a quien no es administrador.
  if (fallo?.estado === 403) {
    return (
      <>
        {encabezado}
        <EmptyState icon={AlertTriangle} title={SIN_ACCESO} data-testid="plantillas-sin-permiso"
          description="Las plantillas de carta las mantiene el administrador del sistema." />
      </>
    );
  }

  const seleccionada = autoridades.find((a) => a.pais === pais);
  // BUG-1101: la vista previa es la de la versión que rige hoy en el país; si ninguna rige todavía
  // (la única empieza en el futuro), la programada. Lo dice el servidor, no la fecha de fin.
  const vigente = versiones?.find((v) => v.estado_de_la_vigencia === 'vigente')
    ?? versiones?.find((v) => v.estado_de_la_vigencia === 'programada') ?? null;
  // La autoridad que se mira. Sin código es la predeterminada y se rotula como siempre, con el
  // nombre de la lista de autoridades; las demás, con el suyo.
  // Solo las del país elegido: al cambiar de país, las del anterior no se pintan ni un instante.
  const deEstePais = delPais?.every((a) => a.pais === pais) ? delPais : null;
  const predeterminada = deEstePais?.find((a) => a.es_predeterminada) ?? null;
  const elegida = codigo ? deEstePais?.find((a) => a.codigo === codigo) ?? null : predeterminada;
  const nombreDeLaAutoridad = codigo && elegida ? elegida.nombre : seleccionada?.autoridad ?? '';
  const hayVarias = (deEstePais?.length ?? 0) > 1;

  return (
    <>
      {encabezado}

      {fallo && (
        <div className="mb-4 space-y-3">
          <Alert tone="danger" title="No se pudo cargar" data-testid="plantillas-error" detalleTecnico={fallo.detalleTecnico}>
            {fallo.message}{fallo.estado === 0 ? ' Revisa tu conexión e intenta refrescar.' : ''}
          </Alert>
          <Button variant="secondary" icon={RotateCcw} data-testid="plantillas-reintentar"
            onClick={() => {
              if (!pais) { cargarAutoridades(); return; }
              cargarVersiones(pais, codigo);
              if (delPais === null) cargarDelPais(pais);
            }}>
            Reintentar
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Card className="xl:col-span-2" title="Autoridades" icon={Landmark} subtitle="Con plantilla asociada, o sin ella.">
          {cargando ? <p data-testid="plantillas-cargando" role="status" className="text-body text-ink-soft">Cargando…</p> : (
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" aria-label="Autoridades">
              {autoridades.map((a) => (
                <li key={a.pais}>
                  <Button variant="ghost" size="sm" className="w-full" aria-pressed={a.pais === pais}
                    data-testid="plantillas-autoridad" onClick={() => { setPais(a.pais); setCodigo(''); }}>
                    <span className="flex w-full flex-wrap items-center gap-2 text-left">
                      <Bandera cod={a.pais} nombre={a.nombre_pais} />
                      <span className={a.pais === pais ? 'font-semibold text-ink' : 'font-medium text-ink'}>{a.autoridad || a.pais}</span>
                      <span className="text-meta text-ink-soft">{a.nombre_pais}</span>
                      <span className="ml-auto">
                        <StatusBadge tone={tonoDelEstadoDePlantilla(a.estado_de_plantilla)} data-testid="plantillas-autoridad-estado"
                          icon={a.estado_de_plantilla === 'sin_plantilla' ? AlertTriangle : undefined}>
                          {a.texto_de_plantilla}
                        </StatusBadge>
                      </span>
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Campos que el sistema rellena" icon={Braces}
          subtitle="Los únicos marcadores que una plantilla puede usar. Uno que no esté aquí se rechaza al publicar.">
          <ul data-testid="plantillas-campos" className="divide-y divide-hairline">
            {campos.map((c) => (
              <li key={c.clave} data-testid="plantillas-campos-item" className="py-1.5 flex flex-wrap items-baseline justify-between gap-2">
                <Mono className="text-meta text-marca-texto">{`{{${c.clave}}}`}</Mono>
                <span className="text-body text-ink">{c.etiqueta}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {seleccionada && (
        <>
          {hayVarias && deEstePais && (
            <div className="mb-4">
              <Card title={`Autoridades de ${seleccionada.nombre_pais}`} icon={Landmark}
                subtitle="Cada autoridad tiene su propia serie de versiones. Elige de cuál ver el historial, publicar o probar.">
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" aria-label={`Plantilla de cada autoridad de ${seleccionada.nombre_pais}`}
                  data-testid="plantillas-autoridades-del-pais">
                  {deEstePais.map((a) => {
                    const activa = a.es_predeterminada ? codigo === '' : codigo === a.codigo;
                    return (
                      <li key={a.codigo}>
                        <Button variant="ghost" size="sm" className="w-full" aria-pressed={activa}
                          data-testid="plantillas-autoridad-del-pais" onClick={() => setCodigo(a.es_predeterminada ? '' : a.codigo)}>
                          <span className="flex w-full flex-wrap items-center gap-2 text-left">
                            <span className={activa ? 'font-semibold text-ink' : 'font-medium text-ink'}>{a.nombre}</span>
                            {a.es_predeterminada && <StatusBadge tone="neutral">{MARCAS_DE_AUTORIDAD.predeterminada}</StatusBadge>}
                            {a.por_confirmar && <StatusBadge tone="warning">{MARCAS_DE_AUTORIDAD.por_confirmar}</StatusBadge>}
                            {!a.activa && <StatusBadge tone="neutral">{MARCAS_DE_AUTORIDAD.desactivada}</StatusBadge>}
                            <span className="ml-auto">
                              <StatusBadge tone={tonoDelEstadoDePlantilla(a.estado_de_plantilla)} data-testid="plantillas-autoridad-del-pais-estado"
                                icon={a.estado_de_plantilla === 'con_plantilla' ? undefined : AlertTriangle}>
                                {a.texto_de_plantilla}
                              </StatusBadge>
                            </span>
                          </span>
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </Card>
            </div>
          )}
          {aviso && <div className="mb-4"><Alert tone="success" title={aviso} data-testid="plantillas-exito" onCerrar={() => setAviso(null)} /></div>}
          {/* Quien lee esto ES Administración: no se le manda a pedírsela a sí mismo. */}
          {versiones !== null && versiones.length === 0 && !codigo && (
            <div className="mb-4">
              <Alert tone="warning" data-testid="plantillas-vacio"
                title={`Falta la plantilla de carta de ${seleccionada.autoridad} (${seleccionada.nombre_pais}): publícala abajo`}>
                Mientras no exista, la carta de un caso de {seleccionada.nombre_pais} no se puede preparar: Asuntos Regulatorios verá «Falta la plantilla de carta de {seleccionada.autoridad} ({seleccionada.nombre_pais}). Pídesela a Administración».
              </Alert>
            </div>
          )}
          {/* CC-02 (RN-81): una autoridad que no es la predeterminada y no tiene plantilla propia. */}
          {versiones !== null && versiones.length === 0 && codigo && elegida && (
            <div className="mb-4">
              <Alert tone="warning" data-testid="plantillas-vacio"
                title={`Falta la plantilla de carta de ${elegida.nombre} (${seleccionada.nombre_pais}): publícala abajo`}>
                {elegida.estado_de_plantilla === 'prestada'
                  ? `${elegida.texto_de_plantilla}. Mientras no exista, la carta a ${elegida.nombre} se prepara con esa plantilla y lo avisa.`
                  : `Mientras no exista, la carta a ${elegida.nombre} no se puede preparar: Asuntos Regulatorios verá «Falta la plantilla de carta de ${elegida.nombre} (${seleccionada.nombre_pais}) y la de ${predeterminada?.nombre ?? seleccionada.autoridad}, la predeterminada. Pídesela a Administración».`}
              </Alert>
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-4">
            <Card title="Historial de versiones" icon={History} subtitle="Cada versión con su vigencia. Todas siguen consultables y descargables.">
              {versiones === null && <p data-testid="plantillas-cargando" role="status" className="text-body text-ink-soft">Cargando…</p>}
              {versiones !== null && versiones.length === 0 && <p className="text-body text-ink-soft">Todavía no hay versiones.</p>}
              {versiones !== null && versiones.length > 0 && (
                <ul className="divide-y divide-hairline" aria-label="Versiones" data-testid="plantillas-historial">
                  {versiones.map((v) => (
                    <li key={v.plantilla_id} data-testid="plantillas-historial-fila" data-estado={v.estado_de_la_vigencia}
                      className="py-2 flex flex-wrap items-center gap-2">
                      <Codigo>{`${nombreDeLaAutoridad} · v${v.version}`}</Codigo>
                      {/* BUG-1101: la frase y el estado los deriva el servidor en el día del país
                          (vigente, programada o anterior); la pantalla no los deduce de la fecha de fin. */}
                      <span className="text-meta text-ink-soft" data-testid="plantillas-historial-vigencia">
                        {v.texto_de_la_vigencia}
                      </span>
                      {v.archivo_nombre && <StatusBadge tone="info" icon={FileText}>{v.archivo_nombre}</StatusBadge>}
                      <span className="ml-auto">
                        <Button size="sm" variant="secondary" icon={Download} onClick={() => { descargar(v.version); }}
                          aria-label={`Descargar versión ${v.version}`} data-testid="plantillas-historial-descargar">
                          Descargar .docx
                        </Button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>
            <PublicarNuevaVersion key={`${pais}/${codigo}`} pais={pais} codigo={codigo} campos={campos} onPublicada={(p) => {
              setAviso(`Publicada ${nombreDeLaAutoridad} · v${p.version}. La anterior queda en el historial.`);
              cargarVersiones(pais, codigo);
              // La lista de autoridades muestra «SENASA · v2» y la del país el estado de cada
              // una («SANIPES · v1»): se refrescan también desde el servidor (§8.1 R3).
              cargarAutoridades();
              cargarDelPais(pais);
            }} />
          </div>

          {vigente && (
            <VistaPreviaDeCarta pais={pais} codigo={codigo} autoridad={nombreDeLaAutoridad} version={vigente.version}
              campos={vigente.campos_combinables} etiquetaDe={etiquetaDe} />
          )}
        </>
      )}
    </>
  );
}
